import { defineComponent as _defineComponent } from 'vue'
import { computed, toRefs } from 'vue';

import { MonetizationType } from '@/@types/graphql-types';

import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { useSpinningText } from '@/helpers/composables/useSpinningText';

import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';


export default /*#__PURE__*/_defineComponent({
  __name: 'AdsRent',
  props: {
    title: null,
    offers: null,
    seasonNumber: null
  },
  setup(__props: any) {

const props = __props as {
	title: string;
	offers: TitleDetail['offers'];
	seasonNumber?: number | undefined;
};

const { language } = storeToRefs(useLanguageStore());



const { getOfferTextBlock, getInfoTextBlock, getRTLInfoTextBlock } = useSpinningText(toRefs(props));

const text = computed(() => {
	switch (language.value) {
		case 'fi':
			return [
				getInfoTextBlock(
					MonetizationType.Ads,
					'Tällä hetkellä voit katsoa elokuvan',
					'ilmaiseksi mainosten kanssa suoratoistona palvelussa'
				),
				getOfferTextBlock(MonetizationType.Rent, ' tai vuokrata sen netissä palvelussa'),
				'. ',
			];
		case 'ru':
			return [
				getInfoTextBlock(MonetizationType.Ads, 'В настоящее время Вы можете посмотреть', 'на'),
				getOfferTextBlock(MonetizationType.Rent, ' бесплатно с рекламой или взять напрокат на', 'онлайн'),
				'. ',
			];
		case 'it':
			return [
				getInfoTextBlock(
					MonetizationType.Ads,
					'Adesso puoi guardare',
					'in streaming su',
					'gratuitamente con avvisi pubblicitari'
				),
				getOfferTextBlock(MonetizationType.Rent, ' o noleggiarlo online su', 'online'),
				'. ',
			];
		case 'de':
			return [
				getInfoTextBlock(
					MonetizationType.Ads,
					'Du kannst',
					'legal bei',
					'kostenlos mit Werbeunterbrechungen im Stream anschauen'
				),
				getOfferTextBlock(MonetizationType.Rent, ' oder bei', 'online leihen'),
				'. ',
			];
		case 'pt':
			return [
				getInfoTextBlock(
					MonetizationType.Ads,
					'Você pode assistir',
					'legalmente no',
					'gratuitamente com comerciais nos intervalos'
				),
				getOfferTextBlock(MonetizationType.Rent, ' ou no', 'alugando online'),
				'. ',
			];
		case 'fr':
			return [
				getInfoTextBlock(
					MonetizationType.Ads,
					'En ce moment, vous pouvez regarder',
					'en streaming gratuit avec publicités sur'
				),
				getOfferTextBlock(MonetizationType.Rent, ' ou de le louer sur', 'online'),
				'. ',
			];
		case 'es':
			return [
				getInfoTextBlock(
					MonetizationType.Ads,
					'Actualmente, usted es capaz de ver',
					'forma gratuita con anuncios'
				),
				getOfferTextBlock(MonetizationType.Rent, ' o para alquilar en', 'online'),
				'. ',
			];
		case 'ja':
			return [
				getInfoTextBlock(MonetizationType.Ads, 'は広告付きの動画が', 'で無料配信中です。'),
				getOfferTextBlock(MonetizationType.Rent, '', 'で配信版のレンタルができます'),
				'。',
			];
		case 'ko':
			return [
				getOfferTextBlock(
					MonetizationType.Ads,
					'현재 ',
					`에서 광고가 포함된 "${props.title}"의 영상을 무료 배포 중이며 `
				),
				getOfferTextBlock(MonetizationType.Rent, '', '에서 대여가 가능합니다.'),
			];
		case 'ro':
			return [
				getInfoTextBlock(
					MonetizationType.Ads,
					'În prezent, puteți viziona',
					'streaming pe',
					'gratuit cu afișare de anunțuri'
				),
				getOfferTextBlock(MonetizationType.Rent, ' sau să-l închiriați pe', 'online'),
				'. ',
			];
		case 'hu':
			return [
				getInfoTextBlock(
					MonetizationType.Ads,
					'Jelenleg a(z)',
					'adatfolyamként elérhető a(z)',
					'szolgáltatónál ingyenesen reklámokkal'
				),
				getOfferTextBlock(MonetizationType.Rent, ' vagy online kibérelhető itt:'),
				'. ',
			];
		case 'cs':
			return [
				getInfoTextBlock(
					MonetizationType.Ads,
					'Aktuálně máte možnost sledovat',
					'streaming u',
					'zdarma s reklamami'
				),
				getOfferTextBlock(MonetizationType.Rent, ' nebo si jej půjčit u', 'online'),
				'. ',
			];
		case 'bg':
			return [
				getInfoTextBlock(
					MonetizationType.Ads,
					'В момента можете да гледате стрийминг на',
					'в',
					'безплатно с реклами в'
				),
				getOfferTextBlock(MonetizationType.Rent, ' или да го наемете', 'онлайн'),
				'. ',
			];
		case 'ar':
			return [
				getInfoTextBlock(MonetizationType.Ads, 'يمكنكم حالياً مشاهدة', 'على', 'مجاناً بفواصل'),
				getOfferTextBlock(MonetizationType.Rent, ' إعلانية أو تأجيره على', 'online'),
				'. ',
			];
		case 'pt':
			return [
				getInfoTextBlock(
					MonetizationType.Ads,
					'Atualmente você pode assistir',
					'em streaming via',
					'gratuitamente com anúncios'
				),
				getOfferTextBlock(MonetizationType.Rent, ' ou alugar online via'),
				'. ',
			];
		case 'tr':
			return [
				getInfoTextBlock(
					MonetizationType.Ads,
					'',
					'adlı yapımı',
					'üzerinden ücretsiz ama reklamlı olarak veya'
				),
				getOfferTextBlock(MonetizationType.Rent, ' ', 'üzerinden kiralayıp çevrimiçi olarak izleyebilirsiniz'),
				'. ',
			];
		case 'sq':
			return [
				getInfoTextBlock(MonetizationType.Ads, 'Aktualisht, ju mund të shikoni', 'në', 'falas me reklama'),
				getOfferTextBlock(MonetizationType.Rent, ' ose ta merrni me qira në', 'online'),
				'. ',
			];
		case 'pl':
			return [
				getInfoTextBlock(MonetizationType.Ads, 'Obecnie możesz oglądać', 'w streamingu w serwisie'),
				getOfferTextBlock(MonetizationType.Rent, ' za darmo z reklamami lub wypożyczyć online w serwisie'),
				'. ',
			];
		case 'ro':
			return [
				getInfoTextBlock(MonetizationType.Ads, 'Acum poți să vezi gratuit', 'pe', ', cu reclame'),
				getOfferTextBlock(MonetizationType.Rent, ' sau să-l închiriezi online pe'),
				'. ',
			];
		case 'az':
			return [
				getInfoTextBlock(
					MonetizationType.Ads,
					'Hazırda siz',
					'üzərindən',
					'yayımını reklamlarla ödənişsiz izləyə'
				),
				getOfferTextBlock(MonetizationType.Rent, ' və ya', 'saytında onlayn icarəyə götürə bilərsiniz'),
				'. ',
			];
		case 'el':
			return [
				getInfoTextBlock(
					MonetizationType.Ads,
					'Προς το παρόν μπορείτε να παρακολουθήσετε τον τίτλο',
					'με streaming στο',
					'δωρεάν με διαφημίσεις ή'
				),
				getOfferTextBlock(MonetizationType.Rent, ' να τον νοικιάσετε στο', 'online'),
				'. ',
			];
		case 'sr':
			return [
				getInfoTextBlock(
					MonetizationType.Ads,
					'Trenutno možete da gledate',
					'strim na',
					'besplatno sa oglasima ili'
				),
				getOfferTextBlock(MonetizationType.Rent, ' da ga iznajmite nae', 'onlajn'),
				'. ',
			];
		case 'sv':
			return [
				getInfoTextBlock(MonetizationType.Ads, 'För närvarande kan du se', 'streama på', 'gratis med annonser'),
				getOfferTextBlock(MonetizationType.Rent, ' eller hyra den på', 'online'),
				'. ',
			];
		case 'uk':
			return [
				getInfoTextBlock(
					MonetizationType.Ads,
					'Наразі ви можете дивитися трансляцію',
					'на',
					'безкоштовно з рекламою'
				),
				getOfferTextBlock(MonetizationType.Rent, ' або взяти її напрокат онлайн на'),
				'. ',
			];
		case 'ur':
			return [
				getRTLInfoTextBlock(
					MonetizationType.Ads,
					'فی الحال آپ اشتہارات کے ساتھ مفت میں',
					'پر',
					'اسٹریمنگ دیکھنے کے قابل ہیں  یا اسے آن لائن '
				),
				getOfferTextBlock(MonetizationType.Rent, '', 'پر کرائے پر لے سکتے ہیں۔'),
			];
		case 'zh':
			return [
				getRTLInfoTextBlock(MonetizationType.Ads, '目前你可以在', '免费带广告观看'),
				getOfferTextBlock(MonetizationType.Rent, ' 或在', '在线租赁'),
			];
		case 'en':
		default:
			return [
				getInfoTextBlock(
					MonetizationType.Ads,
					'Currently you are able to watch',
					'streaming on',
					'for free with ads'
				),
				getOfferTextBlock(MonetizationType.Rent, ' or rent it on', 'online'),
				'. ',
			];
	}
});

return { __sfc: true,language, props, getOfferTextBlock, getInfoTextBlock, getRTLInfoTextBlock, text }
}

})